import { useGlobalContext } from 'Context';
import React, { useEffect, useState, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { useDataFetcher, EMPTY_GUID } from 'Utilities';
import { AsyncStatus } from 'Models';
import Dropdown, {
  DropdownDbOption,
  DropdownOption,
} from 'v3/Components/Form/Dropdown';

interface ClientSelectorProps {
  onClientChange?: (option: DropdownOption | undefined) => void;
  value?: DropdownOption;
  disableUrlSync?: boolean;
  containerClassName?: string;
}

const ClientSelector: React.FC<ClientSelectorProps> = ({
  onClientChange,
  value,
  disableUrlSync = false,
  containerClassName
}) => {
  const globalcontext = useGlobalContext();
  const currentUser = globalcontext[0]?.currentUser;
  const history = useHistory();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialClientId = queryParams.get('clientId') || undefined;

  const [internalSelectedClient, setInternalSelectedClient] = useState<DropdownOption | undefined>();
  const isUpdatingFromLocalRef = React.useRef(false);
  // Last URL clientId we reported as unusable, so we clear it once and not on every run.
  const clearedUrlIdRef = React.useRef<string | undefined>(undefined);

  // Use prop value if provided, otherwise use internal state
  const selectedClient = value !== undefined ? value : internalSelectedClient;

  const [allClients, clientStatus] = useDataFetcher(
    `${ENDPOINTS.clients.base}/dropdownItems`,
    [],
    ({ data }: any) => data,
    undefined,
    true
  );

  // Memoized: this sits in the URL-sync effect's dep array, and a fresh array on
  // every render would re-run that effect on every render.
  const allowedClientIds = currentUser?.clientIds;
  const clients = React.useMemo(
    () =>
      allClients.filter((client: DropdownDbOption) =>
        allowedClientIds?.includes(client.id)
      ),
    [allClients, allowedClientIds]
  );

  const isSingleClient =
    currentUser?.isClient && currentUser?.clientNames?.length === 1;

  // Sync state with URL (only if not disabled and NOT currently updating from local change)
  useEffect(() => {
    if (!disableUrlSync && clientStatus === AsyncStatus.Success && clients.length > 0 && !isUpdatingFromLocalRef.current) {
      const currentUrlId = queryParams.get('clientId') || undefined;

      if (currentUrlId) {
        const matchedClient = clients.find((c: DropdownDbOption) => c.id === currentUrlId);
        if (matchedClient) {
          const option = {
            id: matchedClient.id,
            value: matchedClient.id,
            displayName: matchedClient.name,
          };
          clearedUrlIdRef.current = undefined;
          if (selectedClient?.id !== matchedClient.id) {
            setInternalSelectedClient(option);
            onClientChange?.(option);
          }
        } else if (clearedUrlIdRef.current !== currentUrlId) {
          // The URL names a client this user cannot access. Consumers seed their
          // filter straight from the URL before this list resolves (see
          // useClientSearchParams), so the unusable selection has to be actively
          // cleared -- checking our own state is not enough, it is still empty here.
          clearedUrlIdRef.current = currentUrlId;
          setInternalSelectedClient(undefined);
          onClientChange?.(undefined);
        }
      } else {
        if (selectedClient !== undefined) {
          setInternalSelectedClient(undefined);
          onClientChange?.(undefined);
        }
      }
    }
  }, [clientStatus, initialClientId, clients, onClientChange, disableUrlSync, selectedClient]);

  const handleClientChange = useCallback(
    (option: DropdownOption | undefined) => {
      const rawValue = option?.id || option?.value;
      const nextId =
        rawValue && rawValue !== EMPTY_GUID && rawValue !== ''
          ? String(rawValue)
          : undefined;

      // Update local state and notify parent
      setInternalSelectedClient(option || undefined);
      onClientChange?.(option || undefined);

      if (!disableUrlSync) {
        // Set lock to prevent sync effect from reverting immediately
        isUpdatingFromLocalRef.current = true;

        // Get fresh params from history location to avoid stale closure data
        const currentParams = new URLSearchParams(history.location.search);
        const currentId = currentParams.get('clientId') || undefined;

        if (nextId !== currentId) {
          const newParams = new URLSearchParams(history.location.search);
          if (nextId) {
            newParams.set('clientId', nextId);
            newParams.set('clientName', String(option?.displayName || ''));
          } else {
            newParams.delete('clientId');
            newParams.delete('clientName');
          }

          history.replace({
            search: newParams.toString(),
          });
        }

        // Release lock after a delay to allow propagation
        setTimeout(() => {
          isUpdatingFromLocalRef.current = false;
        }, 500);
      }
    },
    [history, onClientChange, disableUrlSync]
  );

  if (isSingleClient || clientStatus !== AsyncStatus.Success) {
    return null;
  }

  return (
    <div className={`client-selector-container ${containerClassName || ''}`} style={{ minWidth: '200px', marginLeft: '1rem', zIndex: 9999, }}>
      <Dropdown
        optionalDefaultOption="All Clients"
        value={selectedClient}
        onChange={handleClientChange}
        dbOptions={clients}
        required
      />
    </div>
  );
};

export default React.memo(ClientSelector);
